import Axios from 'axios';
import { queryOptions } from '@tanstack/react-query';
import { buildUrl } from 'Util/FetchUtils';
import { PaymentMethodId } from 'Views/Components/_HumanWritten/Payment/PaymentTypeWrapper';
import { SERVER_URL } from 'Constants';
import { RefundMethod } from 'Models/AppConfigStore';

const PAYMENT_API_URL = `${SERVER_URL}/api/Payment`;

/**
 * Provider-agnostic payment method (card) info — mirrors the backend PaymentMethodInfo DTO.
 * Replaces the legacy import of <c>PaymentMethod</c> from @stripe/stripe-js.
 */
export interface PaymentMethodInfo {
	id: string;
	last4: string;
	brand: string;
	type: string;
}

/**
 * Provider-agnostic capability flags shipped to the frontend in PublicPaymentConfig.
 * Mirrors the backend PaymentProviderCapabilities record.
 */
export interface PaymentProviderCapabilities {
	supportsRefunds: boolean;
	supportsSavedCards: boolean;
	supportsTerminalPayments: boolean;
	supportsDynamicSurcharge: boolean;
	supportsApplePay: boolean;
	supportsGooglePay: boolean;
}

/**
 * Active payment provider identifier. Values match the backend's PaymentProviderType enum
 * (serialised by name via the global JsonStringEnumConverter — see Startup.cs).
 */
export type PaymentProvider = 'NONE' | 'STRIPE' | 'SQUARE' | 'EWAY';

/**
 * Public payment configuration shipped from the backend's get-payment-config endpoint.
 * Mirrors the backend PublicPaymentConfig DTO.
 */
export interface PublicPaymentConfig {
	provider: PaymentProvider;
	publicKey: string;
	accountId?: string;
	currency: string;
	currencyCode: string;
	currencyLocale: string;
	/**
	 * Surcharge percentages keyed by payment method. Frontend uses these so wallet modals
	 * (Apple Pay / Google Pay) can show the surcharge-inclusive total, since the native
	 * wallet sheet opens before the server has a chance to recompute.
	 */
	applePayFeePercentage: number;
	googlePayFeePercentage: number;
	otherCardFeePercentage: number;
	capabilities: PaymentProviderCapabilities;
	isSandbox: boolean;
}

export interface PaymentProviderTransactionDetails {
	formActionUrl: string;
	paymentIntentId: string;
	paymentIntentClientSecret: string;
}

/**
 * Ceiling on every request the payment screen waits for.
 *
 * There is no global axios timeout in this app, so without one of these a request that is accepted
 * but never answered — the usual behaviour of a proxy that blackholes traffic rather than refusing
 * it — leaves the promise pending forever. Every gate on the payment screen is downstream of one of
 * these calls, so one unanswered request was an unbounded loading spinner with no error and no
 * retry. Generous enough not to trip on a slow connection; finite so the UI can always move on.
 */
export const PAYMENT_REQUEST_TIMEOUT_MS = 30_000;

export async function setupPayment(
	transactionId: string,
	redirectUrl: string,
	isEventBooking: boolean,
	inCheckIn: boolean,
	selectedRefundMethod?: RefundMethod,
) {
	const res = Axios.post(
		`${PAYMENT_API_URL}/setup-payment`,
		{
			transactionId,
			redirectUrl,
			isEventBooking,
			inCheckIn,
			selectedRefundMethod,
		},
		{ timeout: PAYMENT_REQUEST_TIMEOUT_MS },
	);
	return res;
}

export async function createPaymentIntent(
	transactionId: string,
	redirectUrl: string,
	isEventBooking: boolean,
	inCheckIn: boolean,
	confirmationTokenId?: string,
	paymentMethodId?: string,
	/** Square-specific — payment nonce produced by the Web Payments SDK. */
	nonce?: string,
	/** Square-specific — card brand reported by the Web Payments SDK at tokenize time. */
	cardBrand?: string,
	/** Square-specific — verification token from `payments.verifyBuyer()`, required for saving cards. */
	verificationToken?: string,
) {
	const res = await Axios.post(`${PAYMENT_API_URL}/create-payment-intent`, {
		transactionId,
		redirectUrl,
		confirmationTokenId,
		isEventBooking,
		inCheckIn,
		paymentMethodId,
		nonce,
		cardBrand,
		verificationToken,
	});
	return res;
}

export interface DynamicSurchargeResult {
	dynamicSurcharge: number;
	hasChanged: boolean;
	refresh: boolean;
}

export async function calculateDynamicSurcharge(
	transactionId: string,
	isEventBooking: boolean,
	cardBrand?: string,
	confirmationTokenId?: string,
): Promise<DynamicSurchargeResult> {
	const res = await Axios.post<DynamicSurchargeResult>(`${PAYMENT_API_URL}/calculate-dynamic-surcharge`, {
		transactionId,
		isEventBooking,
		cardBrand,
		confirmationTokenId,
	});
	return res.data;
}

export async function completePayment(paymentIntentId: string, inCheckIn: boolean) {
	return Axios.post(
		buildUrl(`${PAYMENT_API_URL}/complete-payment`, {
			paymentIntentId,
			inCheckIn: inCheckIn.toString(),
		}),
	);
}

/**
 * Resolves the unpaid transaction id backing a booking, for owner self-pay on an
 * unpaid booking. Owner-or-staff guarded server-side.
 * @param bookingId The booking whose unpaid transaction should be resolved.
 */
export async function getUnpaidTransactionForBooking(bookingId: string): Promise<string> {
	const { data } = await Axios.get<string>(
		buildUrl(`${PAYMENT_API_URL}/unpaid-transaction-for-booking`, { bookingId }),
	);
	return data;
}

/**
 * Set transaction to PENDING_INVOICE, set all related alterations to complete.
 * @param transactionId The transaction to be invoiced.
 */
export async function invoicePayment(transactionId: string, eventBooking: boolean, inCheckIn: boolean) {
	const queryParams: any = { transactionId, eventBooking, inCheckIn };
	return Axios.post(buildUrl(`${PAYMENT_API_URL}/invoice-payment`, queryParams));
}

/**
 * Defers payment, splitting the cart so each booking becomes its own unpaid transaction.
 * Returns every resulting transaction id (the original first) so the success page can render
 * them all.
 */
export async function payLater(transactionId: string, inCheckIn: boolean): Promise<string[]> {
	const queryParams: any = { transactionId, inCheckIn };
	const { data } = await Axios.post<string[]>(buildUrl(`${PAYMENT_API_URL}/pay-later`, queryParams));
	return data ?? [];
}

export async function cashPayment(transactionId: string, inCheckIn: boolean) {
	const queryParams: any = { transactionId, inCheckIn };
	return Axios.post(buildUrl(`${PAYMENT_API_URL}/cash-payment`, queryParams));
}

export async function eftposPayment(transactionId: string, inCheckIn: boolean) {
	const queryParams: any = { transactionId, inCheckIn };
	return Axios.post(buildUrl(`${PAYMENT_API_URL}/eftpos-payment`, queryParams));
}

export async function createTerminalPayment(
	transactionId: string,
	terminal: string,
	eventBooking: boolean,
	inCheckIn: boolean,
) {
	return Axios.post(`${PAYMENT_API_URL}/create-terminal-payment`, {
		transactionId: transactionId,
		terminal: terminal,
		isEventBooking: eventBooking,
		inCheckIn: inCheckIn,
	});
}

export async function checkTerminalPayment(transactionId: string) {
	return Axios.post(`${PAYMENT_API_URL}/check-terminal-payment`, {
		transactionId: transactionId,
	});
}

export async function cancelTerminalPayment(transactionId: string) {
	return Axios.post(`${PAYMENT_API_URL}/cancel-terminal-payment`, {
		transactionId: transactionId,
	});
}

export interface TerminalDto {
	id: string;
	label: string;
}
export async function getTerminals() {
	return Axios.get<TerminalDto[]>(`${PAYMENT_API_URL}/get-terminals`);
}

/**
 * Set transaction to complete, set all related alterations to complete.
 * @param transactionId The transaction to be completed without payment.
 * @param paymentMethod
 * @param bookingId If bookingId is provided, update checkedIn status to true.
 */
export async function completeTransactionNoPayment(
	transactionId: string,
	paymentMethod: PaymentMethodId,
	bookingId?: string,
	/**
	 * The refund method staff picked, when the transaction is a refund and they were offered a choice.
	 * The server honours it only if the acting role is allowed that method, and otherwise resolves one
	 * itself — so this is a preference, not an instruction.
	 */
	selectedRefundMethod?: 'Bank' | 'Credit' | 'Manual',
) {
	let transactionStatusToSet = 'EFTPOS';
	if (paymentMethod === 'invoice-option') {
		transactionStatusToSet = 'PENDING_INVOICE';
	} else if (paymentMethod === 'pay-later') {
		transactionStatusToSet = 'PENDING_PAYMENT';
	} else if (paymentMethod === 'cash-option') {
		transactionStatusToSet = 'CASH';
	} else if (paymentMethod === 'credit-card-option') {
		transactionStatusToSet = 'COMPLETED';
	}

	const queryParams: any = { transactionId, transactionStatus: transactionStatusToSet };
	if (bookingId) {
		queryParams.bookingId = bookingId;
	}
	if (selectedRefundMethod) {
		queryParams.selectedRefundMethod = selectedRefundMethod;
	}
	return Axios.post(buildUrl(`${PAYMENT_API_URL}/complete-payment-check-in`, queryParams));
}

// TODO fix to work with Stripe
/**
 * Check that the booking hasn't already had payment taken for
 * it before continuing through to making a new alteration on the booking.
 * @param transactionId The transaction to check if payment has already been taken for.
 */
export async function checkPaymentNotTaken(transactionId: string) {
	return Axios.post(buildUrl(`${PAYMENT_API_URL}/check-no-payment-taken`, { transactionId }));
}

/**
 * Unified provider-agnostic config endpoint. Returns the active provider, its public key,
 * currency, and capability flags. The frontend uses this to decide which provider SDK to load.
 */
export async function fetchPaymentConfig(): Promise<PublicPaymentConfig> {
	const results = await Axios.get(`${PAYMENT_API_URL}/get-payment-config`, {
		timeout: PAYMENT_REQUEST_TIMEOUT_MS,
	});

	return results.data as PublicPaymentConfig;
}

/**
 * @deprecated Use {@link fetchPaymentConfig} instead. Retained for backwards compatibility
 * during the Phase A frontend cutover.
 */
export async function fetchPublishableKey(): Promise<string> {
	const results = await Axios.get(`${PAYMENT_API_URL}/get-payment-publishable-key`);

	return results.data;
}

/**
 * @deprecated Use {@link fetchPaymentConfig} instead. Retained for backwards compatibility
 * during the Phase A frontend cutover.
 */
export async function fetchAccountId(): Promise<string> {
	const results = await Axios.get(`${PAYMENT_API_URL}/get-payment-account-id`);

	return results.data;
}

export async function getPreviousCards(userId?: string): Promise<PaymentMethodInfo[]> {
	const results = await Axios.get(
		buildUrl(`${PAYMENT_API_URL}/get-previous-cards`, {
			userId: userId ?? '',
		}),
	);

	return (results.data ?? []) as PaymentMethodInfo[];
}

/**
 * A customer's saved cards, as the payment step reads them. Shared with the booking wizard's prefetch
 * of the same list (PaymentTab), so the payment step finds it already loaded. Holds the raw card info
 * rather than rendered options so the prefetch needs nothing from the component.
 */
export function previousCardsQueryOptions(userId: string) {
	return queryOptions({
		queryKey: ['previous-cards', userId],
		queryFn: () => getPreviousCards(userId),
	});
}

export interface CurrencyDto {
	currency: string;
	currencyCode: string;
	currencyLocale: string;
}

export async function fetchCurrency(): Promise<CurrencyDto> {
	const results = await Axios.get(`${PAYMENT_API_URL}/get-currency`);

	return results.data as CurrencyDto;
}
